import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
    return twMerge(clsx(inputs))
}

/** Resumo do Laravel quando há vários erros de validação. */
const LARAVEL_MORE_ERRORS = /\s*\(and \d+ more errors?\)\s*$/i

/** Mensagens antigas do pacote pt_BR (fallback se a API ainda devolver texto legado). */
const LARAVEL_REQUIRED_LONG =
    /^É obrigatória a indicação de um valor para o campo (.+)\.\s*$/i
const LARAVEL_REQUIRED_SHORT = /^Campo (.+) obrigatório\.\s*$/i

export function ucfirst(value) {
    const text = String(value ?? '').trim()
    if (!text) return text
    return text.charAt(0).toLocaleUpperCase('pt-BR') + text.slice(1)
}

function requiredFieldMessage(fieldName) {
    return `${ucfirst(fieldName)} é um campo obrigatório.`
}

/** Normaliza texto de validação para exibição curta. */
export function shortenValidationMessage(text) {
    const trimmed = String(text ?? '').trim()
    if (!trimmed) return trimmed

    const longMatch = trimmed.match(LARAVEL_REQUIRED_LONG)
    if (longMatch) return requiredFieldMessage(longMatch[1])

    const shortMatch = trimmed.match(LARAVEL_REQUIRED_SHORT)
    if (shortMatch) return requiredFieldMessage(shortMatch[1])

    const currentMatch = trimmed.match(/^(.+) é um campo obrigatório\.\s*$/i)
    if (currentMatch) return requiredFieldMessage(currentMatch[1])

    return trimmed.replace(LARAVEL_MORE_ERRORS, '').trim()
}

/** Lista todas as mensagens de erro da API (422 etc.), sem agrupar. */
export function getApiErrorMessages(error, fallback = 'Ocorreu um erro.') {
    const data = error?.response?.data
    if (!data) return [fallback]

    const { errors } = data
    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        const messages = []
        for (const value of Object.values(errors)) {
            if (Array.isArray(value)) {
                value.forEach((m) => {
                    const text = m != null ? shortenValidationMessage(m) : ''
                    if (text) messages.push(text)
                })
            } else {
                const text = value != null ? shortenValidationMessage(value) : ''
                if (text) messages.push(text)
            }
        }
        const unique = [...new Set(messages)]
        if (unique.length > 0) return unique
    }

    if (data.message) {
        const msg = shortenValidationMessage(data.message)
        if (msg) return [msg]
    }

    return [fallback]
}

export function formatBRL(value) {
    const n = typeof value === 'number' ? value : Number(value)
    const safe = Number.isFinite(n) ? n : 0
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(safe)
}

const QUANTITY_FORMAT = { minimumFractionDigits: 0, maximumFractionDigits: 3 }

function roundQuantity(n) {
    return Math.round(n * 1000) / 1000
}

/** Quantidades de estoque/BOM (até 3 casas decimais). */
export function formatQuantity(value) {
    if (value === null || value === undefined || value === '') return '-'
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n)) return '-'
    return roundQuantity(n).toLocaleString('pt-BR', QUANTITY_FORMAT)
}

/** Valor para campos de entrada (sem separador de milhar). */
export function formatQuantityInput(value) {
    if (value === null || value === undefined || value === '') return ''
    const n = typeof value === 'number' ? value : Number(normalizeDecimalInputValue(value))
    if (!Number.isFinite(n)) return ''
    return String(roundQuantity(n))
}

/** Movimentação de estoque com sinal (+/-). */
export function formatSignedQuantity(value) {
    if (value === null || value === undefined || value === '') return '-'
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n)) return '-'
    if (n === 0) return formatQuantity(0)
    const abs = formatQuantity(Math.abs(n))
    return n > 0 ? `+${abs}` : `-${abs}`
}

export function parseQuantityInput(value) {
    if (value === null || value === undefined || value === '') return NaN
    const n = parseFloat(normalizeDecimalInputValue(value))
    if (!Number.isFinite(n)) return NaN
    return roundQuantity(n)
}

/** Vírgula decimal (pt-BR) → ponto para armazenamento/parsing. */
export function normalizeDecimalInputValue(value) {
    return String(value).replace(',', '.')
}

export function isValidDecimalInputString(value, maxDecimals = 3) {
    if (value === '' || value === '-') return true
    const pattern = new RegExp(`^-?\\d*(\\.\\d{0,${maxDecimals}})?$`)
    return pattern.test(value)
}

export function isDecimalNumberInput({ type, step, decimal }) {
    if (decimal) return true
    if (type !== 'number') return false
    if (step === undefined || step === null || step === '') return false
    if (step === 'any' || step === '0.001' || step === 0.001) return true
    const s = String(step)
    if (s === '1') return false
    return s.includes('.') || Number(step) < 1
}

/** Casas decimais permitidas a partir do `step` (ex.: "0.01" -> 2, "0.001" -> 3). */
export function decimalPlacesFromStep(step, fallback = 3) {
    if (step === undefined || step === null || step === '' || step === 'any') return fallback
    const s = String(step)
    const idx = s.indexOf('.')
    return idx === -1 ? 0 : s.length - idx - 1
}

/** YYYY-MM-DD no fuso local do navegador (evita UTC de toISOString). */
export function localDateISO(date = new Date()) {
    const d = date instanceof Date ? date : new Date(date)
    if (Number.isNaN(d.getTime())) return ''
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Data civil (YYYY-MM-DD) sem deslocamento UTC.
 * `new Date('2025-05-01')` é meia-noite UTC e vira 30/04 no Brasil.
 */
export function parseCalendarDate(value) {
    if (value == null || value === '') return null
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value
    }
    const s = String(value).trim()
    const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnly) {
        const d = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        return Number.isNaN(d.getTime()) ? null : d
    }
    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? null : d
}

export function formatDateTimeBR(value) {
    if (!value) return '-'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export function formatDateBR(value) {
    const d = parseCalendarDate(value)
    if (!d) return '-'
    return d.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })
}

/** Minutos inteiros em hh:mm. */
export function formatDuration(minutes) {
    if (minutes == null || minutes === '') return '-'
    const total = Number(minutes)
    if (!Number.isFinite(total)) return '-'
    const pad = (n) => String(n).padStart(2, '0')
    return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`
}

export function formatTimeBR(value) {
    if (!value) return '-'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
    })
}

